import {getId, getType} from 'apis/model/base'
import {LoginContext} from 'components/LoginContext'
import classNames from 'classnames'
import useTranslate from 'modules/local/useTranslate'
import React, {useCallback, useContext, useEffect, useMemo, useRef, useState,} from 'react'
import {BiCheck, BiChevronDown, BiGlobe, BiLock, BiSearch, BiSolidChurch, BiX} from 'react-icons/bi'
import {RiUserFill} from 'react-icons/ri'
import {PostFormContext} from './PostFormContext'
import {useFormikContext} from 'formik'
import {ChannelIcon} from "../../icons/ChannelIcon";

// ─── Constants ────────────────────────────────────────────────────────────────

export const PrivacyTypes = Object.freeze({
    PUBLIC:  1,
    PRIVATE: 2,
})

// ─── Helpers ──────────────────────────────────────────────────────────────────

const getLabel  = (item) => item?.title ?? item?.name ?? item?.username ?? item?.idname ?? ''
const getAvatar = (item) => item?.avatar ?? item?.photo ?? null

/**
 * Resolve creator options from a channel option.
 * Falls back to login user if creators array is empty.
 */
const resolveCreatorOptions = (channelOption, login) => {
    const creators = channelOption?.data?.creators ?? []

    if (creators.length > 0) {
        return creators.map((c) => {
            const owner = c?.owner ?? c
            return {
                value: getId(owner),
                label: getLabel(owner),
                data:  owner,
                ownerType: getType(owner),
            }
        })
    }

    // Fallback: login user
    if (login) {
        return [{
            value: getId(login),
            label: getLabel(login),
            data:  login,
            ownerType: getType(login) || 'user',
        }]
    }

    return []
}

// ─── Avatar atom ──────────────────────────────────────────────────────────────

const AvatarIcon = ({ src, icon, size = 28, rounded = 'full' }) => (
    <span
        className={classNames(
            'flex items-center justify-center flex-shrink-0 overflow-hidden background-200',
            rounded === 'md' ? 'rounded-md' : 'rounded-full'
        )}
        style={{ width: size, height: size, minWidth: size }}>
        {src
            ? <img src={src} alt="" className="w-full h-full object-cover"
                   onError={(e) => { e.target.style.display = 'none' }} />
            : <span className="text-primary flex items-center justify-center">{icon}</span>
        }
    </span>
)

// ─── Privacy toggle ───────────────────────────────────────────────────────────

const PrivacyToggle = ({ value, onChange }) => {
    const t = useTranslate()
    const opts = [
        { value: PrivacyTypes.PUBLIC,  label: t('public'),  Icon: BiGlobe },
        { value: PrivacyTypes.PRIVATE, label: t('private'), Icon: BiLock  },
    ]
    return (
        <div className="flex rounded-lg border border-color-200 overflow-hidden">
            {opts.map(({ value: v, label, Icon }) => (
                <button
                    key={v}
                    type="button"
                    onClick={() => onChange(v)}
                    className={classNames(
                        'flex-1 flex items-center justify-center gap-2 py-2 px-3 text-sm font-medium transition-colors',
                        value === v
                            ? 'bg-primary text-white'
                            : 'text-color-500 hover:background-100'
                    )}>
                    <Icon size={14} />
                    <span>{label}</span>
                </button>
            ))}
        </div>
    )
}

// ─── Dropdown ─────────────────────────────────────────────────────────────────

const Dropdown = ({
                      options = [],
                      value,
                      onChange,
                      placeholder,
                      disabled = false,
                      loading  = false,
                      searchable = false,
                      searchValue = '',
                      onSearch,
                      renderOption,
                      renderSelected,
                  }) => {
    const [open, setOpen] = useState(false)
    const wrapRef = useRef(null)

    useEffect(() => {
        const handler = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false)
        }
        document.addEventListener('mousedown', handler)
        return () => document.removeEventListener('mousedown', handler)
    }, [])

    const selected = options.find((o) => o.value === value)

    return (
        <div ref={wrapRef} className="relative w-full">
            <button
                type="button"
                disabled={disabled}
                onClick={() => !disabled && setOpen((v) => !v)}
                className={classNames(
                    'w-full flex items-center justify-between px-3 py-2 rounded-lg border text-sm transition-colors',
                    disabled
                        ? 'border-color-100 background-100 text-color-300 cursor-not-allowed'
                        : 'border-color-200 background hover:border-primary cursor-pointer',
                    open && !disabled && 'border-primary'
                )}>
                <span className="flex-1 min-w-0 text-left">
                    {selected
                        ? (renderSelected ? renderSelected(selected) : <span className="truncate">{selected.label}</span>)
                        : <span className="text-color-300 italic">{placeholder}</span>
                    }
                </span>
                <BiChevronDown
                    size={16}
                    className={classNames(
                        'flex-shrink-0 text-color-400 ml-2 transition-transform duration-150',
                        open && 'rotate-180'
                    )}
                />
            </button>

            {open && (
                <div className="absolute z-50 w-full mt-1 background border border-color-200 rounded-lg shadow-lg overflow-hidden">
                    {searchable && (
                        <div className="p-2 border-b border-color-100">
                            <div className="flex items-center gap-2 px-2 py-1 rounded border border-color-200 background-100">
                                <BiSearch size={13} className="text-color-400 flex-shrink-0" />
                                <input
                                    autoFocus
                                    value={searchValue}
                                    onChange={(e) => onSearch?.(e.target.value)}
                                    placeholder="Search..."
                                    className="flex-1 bg-transparent text-sm outline-none text-color-700 placeholder-color-300"
                                />
                                {!!searchValue && (
                                    <button type="button" onClick={() => onSearch?.('')}
                                            className="text-color-300 hover:text-color-500">
                                        <BiX size={13} />
                                    </button>
                                )}
                            </div>
                        </div>
                    )}

                    <div className="max-h-48 overflow-y-auto">
                        {loading ? (
                            <div className="py-6 text-center text-sm text-color-400">Loading...</div>
                        ) : options.length === 0 ? (
                            <div className="py-6 text-center text-sm text-color-400">No results</div>
                        ) : (
                            options.map((opt) => (
                                <button
                                    key={opt.value}
                                    type="button"
                                    onClick={() => { onChange(opt); setOpen(false) }}
                                    className={classNames(
                                        'w-full flex items-center justify-between px-3 py-2 text-sm text-left transition-colors',
                                        opt.value === value ? 'background-100' : 'hover:background-100'
                                    )}>
                                    <span className="flex-1 min-w-0">
                                        {renderOption ? renderOption(opt) : <span className="truncate">{opt.label}</span>}
                                    </span>
                                    {opt.value === value && (
                                        <BiCheck size={15} className="text-primary flex-shrink-0 ml-2" />
                                    )}
                                </button>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    )
}

// ─── Modal ────────────────────────────────────────────────────────────────────

const PostSettingModal = ({ defaultSelection, onConfirm, onCancel }) => {
    const t = useTranslate()
    const login = useContext(LoginContext)
    const { options, isLoading, keyword, setKeyword } = useContext(PostFormContext)

    const [selectedChannelId, setSelectedChannelId] = useState(
        defaultSelection?.container ? getId(defaultSelection.container) : null
    )
    const [selectedCreatorId, setSelectedCreatorId] = useState(
        defaultSelection?.creator ? getId(defaultSelection.creator) : null
    )
    const [privacy, setPrivacy] = useState(defaultSelection?.privacy ?? PrivacyTypes.PUBLIC)

    const selectedChannelOption = useMemo(
        () => options.find((o) => o.value === selectedChannelId) ?? null,
        [options, selectedChannelId]
    )

    // Creator options: from channel.creators OR fallback to login user
    const creatorOptions = useMemo(
        () => resolveCreatorOptions(selectedChannelOption, login),
        [selectedChannelOption, login]
    )

    // Auto-select creator when channel changes
    useEffect(() => {
        if (creatorOptions.length === 0) {
            setSelectedCreatorId(null)
            return
        }
        const inList = creatorOptions.find((c) => c.value === selectedCreatorId)
        if (!inList) {
            setSelectedCreatorId(creatorOptions[0].value)
        }
    }, [creatorOptions])

    // ── Render helpers ──────────────────────────────────────────────────────

    const renderChannel = useCallback((opt) => (
        <div className="flex items-center gap-2">
            <AvatarIcon
                size={22}
                // src={getAvatar(opt.data?.owner)}
                icon={<ChannelIcon size={12} />}
            />
            <span className="truncate text-sm">{opt.label}</span>
            {opt.is_default === 1 && (
                <span className="text-xs text-primary border border-primary rounded px-1 ml-auto flex-shrink-0">
                    {t('default')}
                </span>
            )}
        </div>
    ), [t])

    const renderCreator = useCallback((opt) => (
        <div className="flex items-center gap-2">
            <AvatarIcon
                size={22}
                src={getAvatar(opt.data)}
                rounded={opt.ownerType === 'organization' ? 'md' : 'full'}
                icon={
                    opt.ownerType === 'organization'
                        ? <BiSolidChurch size={12} />
                        : <RiUserFill size={12} />
                }
            />
            <span className="truncate text-sm">{opt.label}</span>
        </div>
    ), [])

    // ── Confirm ─────────────────────────────────────────────────────────────

    const handleConfirm = useCallback(() => {
        if (!selectedChannelOption) return
        const creatorOpt = creatorOptions.find((c) => c.value === selectedCreatorId)
        onConfirm({
            container: selectedChannelOption.data,
            creator:   creatorOpt?.data ?? login ?? null,
            privacy,
        })
    }, [selectedChannelOption, creatorOptions, selectedCreatorId, privacy, login, onConfirm])

    const canConfirm = Boolean(selectedChannelOption)

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center"
            style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}>
            <div className="background rounded-xl shadow-2xl w-full mx-4" style={{ maxWidth: 460 }}>

                {/* Header */}
                <div className="flex items-start justify-between px-6 pt-5 pb-4 border-b border-color-100">
                    <div>
                        <h3 className="text-base font-bold text-color-000 leading-tight">
                            {t('post settings')}
                        </h3>
                        <p className="text-sm text-color-400 mt-1">
                            {t('please fill in the details')}
                        </p>
                    </div>
                    <button type="button" onClick={onCancel}
                            className="text-color-300 hover:text-color-600 transition-colors mt-1">
                        <BiX size={20} />
                    </button>
                </div>

                {/* Body */}
                <div className="px-6 py-5 flex flex-col gap-4">
                    <div className="flex flex-col gap-2">
                        <label className="text-sm font-semibold text-color-600">
                            {t('content channel')}
                        </label>
                        <Dropdown
                            options={options}
                            value={selectedChannelId}
                            loading={isLoading}
                            placeholder={t('select channel...')}
                            searchable
                            searchValue={keyword}
                            onSearch={setKeyword}
                            renderOption={renderChannel}
                            renderSelected={renderChannel}
                            onChange={(opt) => setSelectedChannelId(opt.value)}
                        />
                    </div>

                    <div className="flex flex-col gap-2">
                        <label className="text-sm font-semibold text-color-600">
                            {t('creator')}
                        </label>
                        <Dropdown
                            options={creatorOptions}
                            value={selectedCreatorId}
                            disabled={!selectedChannelOption}
                            placeholder={t('please select a channel first...')}
                            renderOption={renderCreator}
                            renderSelected={renderCreator}
                            onChange={(opt) => setSelectedCreatorId(opt.value)}
                        />
                    </div>

                    <div className="flex flex-col gap-2">
                        <label className="text-sm font-semibold text-color-600">
                            {t('privacy')}
                        </label>
                        <PrivacyToggle value={privacy} onChange={setPrivacy} />
                    </div>
                </div>

                {/* Footer */}
                <div className="flex items-center gap-3 px-6 pb-5">
                    <button
                        type="button"
                        onClick={onCancel}
                        className="flex-1 py-2 px-4 rounded-lg border border-color-200 text-sm font-medium text-color-600 hover:background-100 transition-colors">
                        {t('cancel')}
                    </button>
                    <button
                        type="button"
                        disabled={!canConfirm}
                        onClick={handleConfirm}
                        className={classNames(
                            'flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors',
                            canConfirm
                                ? 'bg-primary text-white hover:opacity-90'
                                : 'background-200 text-color-300 cursor-not-allowed'
                        )}>
                        {t('confirm')}
                    </button>
                </div>
            </div>
        </div>
    )
}

// ─── Summary label ────────────────────────────────────────────────────────────

const PostSettingSummary = ({ current, onClick }) => {
    const t = useTranslate()
    const { container, creator, privacy } = current ?? {}
    const PrivacyIcon = privacy === PrivacyTypes.PRIVATE ? BiLock : BiGlobe

    if (!container) {
        return (
            <button
                type="button"
                onClick={onClick}
                className="flex items-center gap-2 px-3 py-2 rounded-lg border border-dashed border-color-300 hover:border-primary hover:text-primary transition-colors text-sm text-color-400 w-full">
                <BiSearch size={14} />
                <span>{t('select channel to post...')}</span>
            </button>
        )
    }

    const creatorType = getType(creator)

    return (
        <button
            type="button"
            onClick={onClick}
            className="flex items-center gap-3 px-3 py-2 rounded-lg background-100 bg-post-setting border border-color-100 hover:border-primary transition-colors w-full group text-left">
            <AvatarIcon
                size={34}
                icon={<ChannelIcon size={16} />}
            />
            <div className="flex flex-col flex-1 min-w-0">
                <span className="text-sm font-semibold text-color-700 truncate group-hover:text-primary transition-colors leading-tight">
                    {getLabel(container)}
                </span>
                {creator && (
                    <div className="flex items-center gap-1 mt-1">
                        <AvatarIcon
                            size={14}
                            src={getAvatar(creator)}
                            rounded={creatorType === 'organization' ? 'md' : 'full'}
                            icon={
                                creatorType === 'organization'
                                    ? <BiSolidChurch size={8} />
                                    : <RiUserFill size={8} />
                            }
                        />
                        <span className="text-xs text-color-400 truncate leading-tight">
                            {getLabel(creator)}
                        </span>
                    </div>
                )}
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
                <PrivacyIcon size={13} className="text-color-400" />
                <BiChevronDown size={15} className="text-color-400 group-hover:text-primary transition-colors" />
            </div>
        </button>
    )
}

// ─── Main export ──────────────────────────────────────────────────────────────

/**
 * PostSettingForm
 *
 * Reads/writes PostFormContext.current.
 * Place inside any component wrapped by <PostFormProvider>.
 * No props needed.
 */

// FormikAwareWrapper — tries to render children (which use useFormikContext).
// If Formik context is not available, renders fallback instead.
// Uses a class-based ErrorBoundary since hooks can't catch render errors.
class FormikAwareWrapper extends React.Component {
    constructor(props) {
        super(props)
        this.state = { hasError: false }
    }
    static getDerivedStateFromError() {
        return { hasError: true }
    }
    render() {
        if (this.state.hasError) {
            return this.props.fallback ?? null
        }
        return this.props.children
    }
}

// Inner component — only rendered when inside a Formik context.
// Safely calls useFormikContext and syncs values on confirm.
const PostSettingFormWithFormik = ({ onConfirm }) => {
    const { setFieldValue } = useFormikContext()

    const { options } = useContext(PostFormContext)

    const handleConfirm = useCallback(
        (selection) => {
            const { container, creator, privacy } = selection ?? {}

            const channelOpt = options.find((o) => o.value === getId(container))
            if (channelOpt) {
                setFieldValue('container', channelOpt)
            }
            if (creator) {
                setFieldValue('owner', {
                    value: getId(creator),
                    data:  creator,
                    id:    getId(creator),
                    type:  getType(creator),
                    label: creator?.name ?? creator?.username ?? '',
                })
            }
            if (privacy !== undefined) {
                setFieldValue('privacy', privacy)
            }

            onConfirm(selection)
        },
        [options, setFieldValue, onConfirm]
    )

    return <PostSettingFormInner onConfirm={handleConfirm} />
}

// Base inner component — no Formik dependency.
const PostSettingFormInner = ({ onConfirm }) => {
    const { current } = useContext(PostFormContext)
    const [open, setOpen] = useState(false)

    const handleConfirm = useCallback(
        (selection) => {
            onConfirm(selection)
            setOpen(false)
        },
        [onConfirm]
    )

    return (
        <>
            <PostSettingSummary current={current} onClick={() => setOpen(true)} />
            {open && (
                <PostSettingModal
                    defaultSelection={current}
                    onConfirm={handleConfirm}
                    onCancel={() => setOpen(false)}
                />
            )}
        </>
    )
}

const PostSettingForm = () => {
    const { current, setCurrent } = useContext(PostFormContext)

    const handleConfirm = useCallback(
        (selection) => { setCurrent(selection) },
        [setCurrent]
    )

    // Detect if we are inside a Formik context by checking the context value.
    // FormikContext always has a setFieldValue function when inside <Formik>.
    // We render the Formik-aware version if available, otherwise the base version.
    return (
        <FormikAwareWrapper
            fallback={<PostSettingFormInner onConfirm={handleConfirm} />}>
            <PostSettingFormWithFormik onConfirm={handleConfirm} />
        </FormikAwareWrapper>
    )
}

export default PostSettingForm